/* The one HMS data table.
 *
 * The real deliverable is still the CSS contract — `.hms-table-wrap`
 * wrapping a `table.hms-table` — because many HMS tables are hand-built
 * markup with sticky columns, grouped headers and inline editors, and
 * they only need the LOOK shared. Those screens keep their own <table>
 * and just adopt the two class names (plus `.hms-table-num` on number
 * cells).
 *
 * This component is the renderer for list tables: columns in, rows in,
 * and it owns sorting, the sticky header, number columns, a row-actions
 * column, and the loading and empty states, so screens stop rebuilding
 * them one by one.
 *
 * Borrowed, not invented:
 *  - Sort header is a <button> inside the <th>, aria-sort on the sorted
 *    column only, one arrow on the sorted column and a faint two-way
 *    arrow on hover of the others (IBM Carbon DataTable, WAI-ARIA APG
 *    sortable table).
 *  - First click sorts ascending, the next click flips it; no third
 *    "unsorted" click (GitHub Primer DataTable, Shopify Polaris
 *    IndexTable).
 *  - Number columns right-aligned with tabular figures so digits line up
 *    (Primer `align: 'end'`, Polaris heading `alignment: 'end'`).
 *  - Row actions sit in the last column (Primer).
 *  - Empty and loading states render inside the table frame, header
 *    kept, so the page does not jump when data lands (Polaris
 *    `emptyState` / `loading`, Carbon DataTableSkeleton).
 *  - `dense` is Primer's `cellPadding="condensed"` / Polaris `condensed`.
 *
 * Props:
 *  - columns:      [{ key, label, align, width, className, numeric,
 *                     sortable, sortValue, cellProps }]
 *                  `align`/`width` land as inline styles on the cells;
 *                  `className` lands on the header AND the body cells.
 *                  `numeric` right-aligns the column with tabular figures.
 *                  `sortable` makes the header a sort button; `sortValue`
 *                  (row) => value is what gets compared, defaulting to
 *                  `row[key]` (numeric columns parse "2,875,000.00").
 *                  `cellProps` (row, rowIndex) => props goes on the body
 *                  <td> only: a td-only className (merged), a click
 *                  handler, a title, data-* attributes.
 *  - rows:         the data; a row is whatever `renderCell` understands
 *  - renderCell:   (row, column, rowIndex) => node — the render prop;
 *                  defaults to `row[column.key]`
 *  - rowKey:       (row, rowIndex) => key; defaults to the index
 *  - rowClassName: (row, rowIndex) => string, for the row modifiers
 *                  (e.g. 'is-overdue')
 *  - onRowClick:   (row, rowIndex) => void; makes rows clickable. Mouse
 *                  only, so anything it opens must also be reachable
 *                  from a row action.
 *  - rowActions:   (row, rowIndex) => node; renders a last column, right-
 *                  aligned, whose clicks never reach onRowClick
 *  - rowActionsLabel: header of that column; defaults to 'Actions'
 *  - sort / onSortChange: controlled sort, { key, direction } with
 *                  direction 'ascending' | 'descending'. When `sort` is
 *                  given the table does NOT reorder rows — the caller
 *                  (or the server) already did.
 *  - defaultSort:  uncontrolled starting sort; the table reorders rows
 *                  itself and still reports changes to onSortChange
 *  - stickyHeader: the header stays put while the body scrolls inside
 *                  the wrapper, which gets a max height (see maxHeight)
 *  - maxHeight:    number (px) or CSS length for the scroll viewport;
 *                  defaults to --table-scroll-max-height when sticky
 *  - dense:        tighter cell padding for long lists
 *  - wrapHeader:   header labels may break onto two lines when the table
 *                  is short of room, instead of pushing the last columns
 *                  out of view (the invoicing lists, 13 columns wide). A
 *                  table that fits never wraps: auto layout only squeezes
 *                  columns when it has to.
 *  - loading:      header plus `loadingRows` skeleton rows
 *  - empty:        what shows when there are no rows: an HMSEmptyState
 *                  props object ({ icon, title, hint, action }) or any
 *                  node
 *  - label:        aria-label for the table
 *  - accent:       cell text colour for this screen; defaults to
 *                  --table-cell-text
 *  - className:    extra hook on the wrapper
 *  - tableClassName: extra hook on the <table>
 */
import React, { useMemo, useState } from 'react';
import HMSEmptyState from './HMSEmptyState';
import HMSSkeleton from './HMSSkeleton';
import './HMSTable.css';

const ASC = 'ascending';
const DESC = 'descending';

const isBlank = (value) => value === null || value === undefined || value === '';

/* "2,875,000.00" and "-1 014 060" both read as numbers on a numeric column. */
const toNumber = (value) => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
};

const sortValueOf = (row, column) => {
  const raw = column.sortValue ? column.sortValue(row) : row[column.key];
  if (raw instanceof Date) return raw.getTime();
  if (column.numeric && !isBlank(raw)) return toNumber(raw);
  return raw;
};

/* Blanks always sink to the bottom, whichever way the column runs, so a
   descending sort never opens on a screen of dashes. */
const compareRows = (column, direction) => (a, b) => {
  const left = sortValueOf(a, column);
  const right = sortValueOf(b, column);
  if (isBlank(left) && isBlank(right)) return 0;
  if (isBlank(left)) return 1;
  if (isBlank(right)) return -1;
  const order = typeof left === 'number' && typeof right === 'number'
    ? left - right
    : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
  return direction === DESC ? -order : order;
};

const len = (value) => (typeof value === 'number' ? `${value}px` : value);

/* One arrow, drawn once: pointing up for ascending, the same path turned
   for descending (Carbon does the same with one ArrowUp). The two-way
   arrow is the idle hint on sortable columns that are not the sorted one. */
const SortIcon = ({ direction }) => (
  <svg
    className={`hms-table-sort-icon${direction ? ` is-${direction}` : ''}`}
    viewBox="0 0 10 10"
    aria-hidden="true"
    focusable="false"
  >
    {direction
      ? <path d="M5 1.5 8.5 6h-7z" />
      : <path d="M5 0.5 8 4H2zM5 9.5 2 6h6z" />}
  </svg>
);

const HMSTable = ({
  columns = [],
  rows = [],
  renderCell,
  rowKey,
  rowClassName,
  onRowClick,
  rowActions,
  rowActionsLabel = 'Actions',
  sort,
  defaultSort,
  onSortChange,
  stickyHeader = false,
  maxHeight,
  dense = false,
  wrapHeader = false,
  loading = false,
  loadingRows = 5,
  empty,
  label,
  accent,
  className = '',
  tableClassName = '',
}) => {
  const [ownSort, setOwnSort] = useState(defaultSort || null);
  const controlled = sort !== undefined;
  const activeSort = controlled ? sort : ownSort;

  const shownRows = useMemo(() => {
    if (controlled || !activeSort) return rows;
    const column = columns.find((c) => c.key === activeSort.key);
    if (!column) return rows;
    return [...rows].sort(compareRows(column, activeSort.direction));
  }, [controlled, activeSort, rows, columns]);

  const handleSort = (column) => {
    const next = activeSort && activeSort.key === column.key
      ? { key: column.key, direction: activeSort.direction === ASC ? DESC : ASC }
      : { key: column.key, direction: ASC };
    if (!controlled) setOwnSort(next);
    if (onSortChange) onSortChange(next);
  };

  const cellClass = (column) => [
    column.numeric ? 'hms-table-num' : '',
    column.className || '',
  ].filter(Boolean).join(' ') || undefined;

  const cellStyle = (column) => {
    const style = {};
    if (column.align) style.textAlign = column.align;
    if (column.width) style.width = column.width;
    return Object.keys(style).length ? style : undefined;
  };

  const columnCount = columns.length + (rowActions ? 1 : 0);

  const wrapClass = [
    'hms-table-wrap',
    stickyHeader ? 'hms-table-wrap--scroll' : '',
    className,
  ].filter(Boolean).join(' ');

  const wrapStyle = {};
  if (accent) wrapStyle['--hms-table-accent'] = accent;
  if (maxHeight !== undefined) wrapStyle.maxHeight = len(maxHeight);

  const tableClass = [
    'hms-table',
    stickyHeader ? 'hms-table--sticky' : '',
    dense ? 'hms-table--dense' : '',
    wrapHeader ? 'hms-table--wrap-head' : '',
    onRowClick ? 'hms-table--clickable' : '',
    tableClassName,
  ].filter(Boolean).join(' ');

  const renderHeader = (column) => {
    const sorted = activeSort && activeSort.key === column.key ? activeSort.direction : null;
    return (
      <th
        key={column.key}
        scope="col"
        className={cellClass(column)}
        style={cellStyle(column)}
        aria-sort={sorted || undefined}
      >
        {column.sortable ? (
          <button
            type="button"
            className={`hms-table-sort${sorted ? ' is-sorted' : ''}`}
            onClick={() => handleSort(column)}
          >
            {column.label}
            <SortIcon direction={sorted} />
          </button>
        ) : column.label}
      </th>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        /* The bars are decoration; the one status line below the table is
           what assistive tech hears. */
        <tbody aria-hidden="true">
          {Array.from({ length: loadingRows }, (unused, rowIndex) => (
            <tr key={rowIndex} className="hms-table-skeleton-row">
              {columns.map((column) => (
                <td key={column.key} className={cellClass(column)} style={cellStyle(column)}>
                  <HMSSkeleton />
                </td>
              ))}
              {rowActions && <td className="hms-table-actions"><HMSSkeleton /></td>}
            </tr>
          ))}
        </tbody>
      );
    }

    if (shownRows.length === 0) {
      const emptyNode = React.isValidElement(empty) || typeof empty === 'string'
        ? empty
        : <HMSEmptyState title="Nothing to show" {...(empty || {})} />;
      return (
        <tbody>
          <tr className="hms-table-empty-row">
            <td colSpan={columnCount || 1}>{emptyNode}</td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody>
        {shownRows.map((row, rowIndex) => (
          <tr
            key={rowKey ? rowKey(row, rowIndex) : rowIndex}
            className={rowClassName ? rowClassName(row, rowIndex) : undefined}
            onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
          >
            {columns.map((column) => {
              const { className: ownClass, ...ownProps } = column.cellProps
                ? column.cellProps(row, rowIndex) || {}
                : {};
              return (
                <td
                  key={column.key}
                  {...ownProps}
                  className={[cellClass(column), ownClass].filter(Boolean).join(' ') || undefined}
                  style={cellStyle(column)}
                >
                  {renderCell ? renderCell(row, column, rowIndex) : row[column.key]}
                </td>
              );
            })}
            {rowActions && (
              <td className="hms-table-actions" onClick={(event) => event.stopPropagation()}>
                {rowActions(row, rowIndex)}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    );
  };

  return (
    <div
      className={wrapClass}
      // The accent rides a custom property so every td inherits it
      // without the component touching each cell.
      style={Object.keys(wrapStyle).length ? wrapStyle : undefined}
    >
      <table className={tableClass} aria-label={label} aria-busy={loading || undefined}>
        <thead>
          <tr>
            {columns.map(renderHeader)}
            {rowActions && (
              <th scope="col" className="hms-table-actions">{rowActionsLabel}</th>
            )}
          </tr>
        </thead>
        {renderBody()}
      </table>
      {loading && <span className="hms-table-sr-only" role="status">Loading</span>}
    </div>
  );
};

export default HMSTable;
